import { get, has } from 'lodash';

import { CHART_CATEGORIES } from 'constants/chart-categories';
import { CHART_COLORS } from 'constants/chart-spec';

import { generateVegaTooltip } from 'utils/chart/tooltips';

import {
  generateVegaSortParams,
  generateVegaSortingAndLimitingTransforms,
  generateVegaTypeCoercionTransforms,
} from 'utils/chart/transforms';

export default {
  name: 'Word Cloud',
  displayName: 'Word Cloud',
  order: 2,
  flavour: 'discrete',
  category: CHART_CATEGORIES.TEXT,
  iconValue: 'word-cloud',
  channels: [
    {
      name: 'text',
      displayName: 'Text',
      required: true,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'size',
      displayName: 'Size',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
    },
    {
      name: 'color',
      displayName: 'Color',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);

    return {
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            // Define a random angle
            {
              type: 'formula',
              as: 'angle',
              expr: '[-45, 0, 45][~~(random() * 3)]',
            },
            // Define a font weight for the text
            {
              type: 'formula',
              as: 'weight',
              expr: '600',
            },
            // Define the size of the text, either from the size channel or a default of 1
            {
              type: 'formula',
              as: 'size',
              expr: 'if(datum.size, datum.size, 1)',
            },
            ...(has(encodings, 'size')
              ? generateVegaTypeCoercionTransforms('size', encodings)
              : []),
            ...generateVegaSortingAndLimitingTransforms(encodings),
          ],
        },
      ],
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      scales: [
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range: has(encodings, 'color')
            ? get(customisations.options, 'colorDiscrete.value') || CHART_COLORS
            : [],
        },
      ],
      marks: [
        {
          type: 'text',
          from: {
            data: 'transformedData',
          },
          encode: {
            enter: {
              text: {
                field: 'text',
              },
              tooltip: {
                signal: generateVegaTooltip(encodings, customisations),
              },
              align: {
                value: 'center',
              },
              baseline: {
                value: 'alphabetic',
              },
              ...(has(encodings, 'color')
                ? {
                    fill: {
                      scale: 'color',
                      field: 'color',
                    },
                  }
                : {}),
            },
            update: {
              fillOpacity: {
                value: 1,
              },
            },
            hover: {
              fillOpacity: {
                value: 0.5,
              },
            },
          },
          transform: [
            {
              type: 'wordcloud',
              size: [{ signal: 'width' }, { signal: 'height' }],
              text: {
                field: 'text',
              },
              rotate: {
                field: 'datum.angle',
              },
              font: 'Akzidenz',
              fontSize: {
                field: 'datum.size',
              },
              fontSizeRange: [12, 56],
              fontWeight: {
                field: 'datum.weight',
              },
              padding: 2,
            },
          ],
        },
      ],
    };
  },
};
